import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { SCO_PITCH_DECK_AUDIT_UPDATED_SUBSCRIPTION, SCO_PITCH_DECK_QUERY, SCO_PITCH_DECK_UPDATED_SUBSCRIPTION } from '@src/components/sco/PitchDeckAuditor/gql/operations';
import { usePitchDeckStaticOptions } from '@src/components/sco/PitchDeckAuditor/hooks/usePitchDeckStaticOptions';
import { adaptDeck } from '@src/components/sco/PitchDeckAuditor/model/adapt';
import { PitchDeckView } from '@src/components/sco/PitchDeckAuditor/model/types';
import { ScoPitchDeck } from '@src/graphql-types';
import { useEffect, useMemo } from 'react';
const PROCESSING_STATUSES = new Set(['uploaded', 'parsing', 'classified', 'auditing']);
const onSubscriptionError = (label: string) => (error: Error) => {
  // The deck/audit live feed is best-effort — a closed socket must not break the page.
  // While processing we also poll as a fallback, so dropped subscriptions self-heal.
  if (process.env.NODE_ENV !== 'production') {
    console.warn(`[PitchDeckAuditor] ${label} subscription closed:`, error?.message);
  }
};

/**
 * Loads a pitch deck by id and keeps it live ONLY while it is still processing:
 *  - scoPitchDeckUpdated      → statusKey / phaseKey / slides / chat sessions during processing
 *  - scoPitchDeckAuditUpdated → audit head + per-slide audit rows as the audit streams in
 * Once the deck is audited/failed the data is static, so we hold no open sockets. A polling
 * fallback covers WS drops, and subscription errors are swallowed (never surface to the page).
 */
export function useScoPitchDeck(pitchDeckId) {
  const $ = _c(28);
  const t0 = !pitchDeckId;
  let t1;
  if ($[0] !== pitchDeckId) {
    t1 = {
      pitchDeckId
    };
    $[0] = pitchDeckId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      skip: t0,
      variables: t1,
      fetchPolicy: "cache-and-network"
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const query = useQuery(SCO_PITCH_DECK_QUERY, t2);
  const {
    subscribeToMore,
    startPolling,
    stopPolling
  } = query;
  const statusKey = query.data?.scoPitchDeck?.statusKey;
  const latestPitchDeckAuditId = query.data?.scoPitchDeck?.latestPitchDeckAuditId;
  const hasStoredFile = !!(query.data?.scoPitchDeck?.deckStoredFileId || query.data?.scoPitchDeck?.deckStoredFile?.storedFileId);
  let t3;
  if ($[5] !== hasStoredFile || $[6] !== statusKey) {
    t3 = hasStoredFile && !!statusKey && PROCESSING_STATUSES.has(statusKey);
    $[5] = hasStoredFile;
    $[6] = statusKey;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const isProcessing = t3;
  let t4;
  let t5;
  if ($[8] !== isProcessing || $[9] !== pitchDeckId || $[10] !== startPolling || $[11] !== stopPolling) {
    t4 = () => {
      if (!pitchDeckId || !isProcessing) {
        return;
      }
      startPolling?.(5000);
      return () => stopPolling?.();
    };
    t5 = [pitchDeckId, isProcessing, startPolling, stopPolling];
    $[8] = isProcessing;
    $[9] = pitchDeckId;
    $[10] = startPolling;
    $[11] = stopPolling;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[14] !== isProcessing || $[15] !== pitchDeckId || $[16] !== subscribeToMore) {
    t6 = () => {
      if (!pitchDeckId || !isProcessing) {
        return;
      }
      const unsubscribe = subscribeToMore({
        document: SCO_PITCH_DECK_UPDATED_SUBSCRIPTION,
        variables: {
          pitchDeckId
        },
        onError: onSubscriptionError("scoPitchDeckUpdated"),
        updateQuery: _temp
      });
      return () => unsubscribe?.();
    };
    t7 = [pitchDeckId, isProcessing, subscribeToMore];
    $[14] = isProcessing;
    $[15] = pitchDeckId;
    $[16] = subscribeToMore;
    $[17] = t6;
    $[18] = t7;
  } else {
    t6 = $[17];
    t7 = $[18];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[19] !== isProcessing || $[20] !== latestPitchDeckAuditId || $[21] !== pitchDeckId || $[22] !== subscribeToMore) {
    t8 = () => {
      if (!pitchDeckId || !latestPitchDeckAuditId || !isProcessing) {
        return;
      }
      const unsubscribe_0 = subscribeToMore({
        document: SCO_PITCH_DECK_AUDIT_UPDATED_SUBSCRIPTION,
        variables: {
          pitchDeckAuditId: latestPitchDeckAuditId
        },
        onError: onSubscriptionError("scoPitchDeckAuditUpdated"),
        updateQuery: _temp2
      });
      return () => unsubscribe_0?.();
    };
    t9 = [pitchDeckId, latestPitchDeckAuditId, isProcessing, subscribeToMore];
    $[19] = isProcessing;
    $[20] = latestPitchDeckAuditId;
    $[21] = pitchDeckId;
    $[22] = subscribeToMore;
    $[23] = t8;
    $[24] = t9;
  } else {
    t8 = $[23];
    t9 = $[24];
  }
  useEffect(t8, t9);
  const t10 = query.data?.scoPitchDeck ?? null;
  let t11;
  if ($[25] !== query || $[26] !== t10) {
    t11 = {
      deck: t10,
      loading: query.loading,
      error: query.error,
      refetch: query.refetch,
      query
    };
    $[25] = query;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
}
function _temp2(prev_0, t0) {
  const {
    subscriptionData: subscriptionData_0
  } = t0;
  const updatedAudit = subscriptionData_0?.data?.scoPitchDeckAuditUpdated?.pitchDeckAudit;
  if (!updatedAudit) {
    return prev_0;
  }
  if (prev_0?.scoPitchDeck?.latestPitchDeckAuditId !== updatedAudit.pitchDeckAuditId) {
    return prev_0;
  }
  return {
    ...prev_0,
    scoPitchDeck: {
      ...prev_0.scoPitchDeck,
      latestPitchDeckAudit: {
        ...(prev_0.scoPitchDeck.latestPitchDeckAudit ?? {}),
        ...updatedAudit
      }
    }
  };
}
function _temp(prev, t0) {
  const {
    subscriptionData
  } = t0;
  const updated = subscriptionData?.data?.scoPitchDeckUpdated?.pitchDeck;
  if (!updated) {
    return prev;
  }
  return {
    ...prev,
    scoPitchDeck: {
      ...(prev?.scoPitchDeck ?? {}),
      ...updated
    }
  };
}
export type UsePitchDeckViewValue = {
  deck: PitchDeckView | null;
  raw: ScoPitchDeck | null;
  loading: boolean;
  error: any;
  refetch: () => any;
};

/** useScoPitchDeck + static options → an adapted `PitchDeckView` ready for the UI. */
export function usePitchDeckView(pitchDeckId) {
  const $ = _c(9);
  const {
    deck,
    loading,
    error,
    refetch
  } = useScoPitchDeck(pitchDeckId);
  const {
    lookups,
    loading: optionsLoading
  } = usePitchDeckStaticOptions();
  let t0;
  if ($[0] !== deck || $[1] !== lookups) {
    t0 = adaptDeck(deck, lookups);
    $[0] = deck;
    $[1] = lookups;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const view = t0;
  const t1 = loading || optionsLoading && !deck;
  let t2;
  if ($[3] !== deck || $[4] !== error || $[5] !== refetch || $[6] !== t1 || $[7] !== view) {
    t2 = {
      deck: view,
      raw: deck,
      loading: t1,
      error,
      refetch
    };
    $[3] = deck;
    $[4] = error;
    $[5] = refetch;
    $[6] = t1;
    $[7] = view;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}